import { setLabel } from './i18n';
import { isChord } from '../controls/keymap';
import { createElement, getUiRoot, setText } from './dom';
import { createIcon, type UiIcon } from './icons';
import type { TourId, TourRoute, TourSnapshot } from '../tours/types';
import { createTourStory, stopTourControlKey } from './tourStory';

export interface TourActions {
  readonly start: (id: TourId) => void;
  readonly pause: () => void;
  readonly restart: () => void;
  readonly seek: (seconds: number) => void;
  readonly speed: (rate: number) => void;
  readonly stop: () => void;
  readonly blocked: () => boolean;
  readonly openGateway: () => void;
}

function button(parent: HTMLElement, label: string, icon?: UiIcon): HTMLButtonElement {
  const el = createElement('button', 'tour-button', parent);
  el.type = 'button';
  setLabel(el, 'aria-label', label);
  setLabel(el, 'title', label);
  if (icon) el.append(createIcon(icon, 18));
  else setText(el, label);
  return el;
}

function time(seconds: number): string {
  const rounded = Math.floor(seconds);
  return `${Math.floor(rounded / 60)}:${String(rounded % 60).padStart(2, '0')}`;
}

function buildMenu(root: HTMLElement, routes: readonly TourRoute[], actions: TourActions) {
  const menu = createElement('dialog', 'tour-menu gateway-panel', root);
  menu.id = 'tour-menu';
  setLabel(menu, 'aria-label', 'Choose a tour');
  menu.hidden = true;
  const header = createElement('div', 'gateway-panel-header', menu);
  setText(createElement('h2', 'tour-heading', header), 'Tours');
  const close = button(header, 'Close tours', 'close');
  const choices = routes.map((route) => {
    const choice = button(menu, route.title, route.kind === 'boat' ? 'boat' : route.kind);
    choice.classList.add('tour-choice');
    setText(createElement('span', 'tour-choice-title', choice), route.title);
    setText(createElement('span', 'tour-duration', choice), time(route.duration));
    if (route.summary) setText(createElement('span', 'tour-summary', choice), route.summary);
    choice.addEventListener('click', () => {
      actions.start(route.id);
    });
    return choice;
  });
  return { menu, close, choices };
}

function speedControl(parent: HTMLElement, actions: TourActions) {
  if (!import.meta.env.DEV) return null;
  const speed = createElement('select', 'tour-speed', parent);
  setLabel(speed, 'aria-label', 'Tour speed');
  setLabel(speed, 'title', 'Tour speed');
  for (const rate of [0.5, 1, 2, 4, 8, 16]) {
    const option = createElement('option', undefined, speed);
    option.value = String(rate);
    option.textContent = `${rate}×`;
  }
  speed.value = '1';
  speed.addEventListener('change', () => {
    actions.speed(Number(speed.value));
  });
  return speed;
}

function buildPlayer(root: HTMLElement, actions: TourActions) {
  const player = createElement('section', 'tour-player', root);
  setLabel(player, 'aria-label', 'Tour playback');
  player.hidden = true;
  const row = createElement('div', 'tour-player-row', player);
  const title = createElement('span', 'tour-title', row);
  const speed = speedControl(row, actions);
  const pause = button(row, 'Pause tour', 'pause');
  const restart = button(row, 'Restart tour', 'restart');
  const stop = button(row, 'Take over');
  const timeline = createElement('div', 'tour-timeline', player);
  const seek = createElement('input', 'tour-seek', timeline);
  seek.type = 'range';
  seek.min = '0';
  seek.step = '0.1';
  setLabel(seek, 'aria-label', 'Tour position');
  const clock = createElement('span', 'tour-clock', timeline);
  pause.addEventListener('click', actions.pause);
  restart.addEventListener('click', actions.restart);
  stop.addEventListener('click', actions.stop);
  seek.addEventListener('input', () => {
    actions.seek(seek.valueAsNumber);
  });
  return { player, title, speed, pause, restart, stop, seek, clock };
}

function buildView(routes: readonly TourRoute[], actions: TourActions) {
  const root = createElement('div', 'tour-controls');
  const launcher = button(root, 'Tours', 'tour');
  launcher.classList.add('tour-launcher');
  setText(createElement('span', undefined, launcher), 'Tours');
  launcher.setAttribute('aria-controls', 'tour-menu');
  launcher.setAttribute('aria-expanded', 'false');
  const menu = buildMenu(root, routes, actions);
  const player = buildPlayer(menu.menu, actions);
  return { root, launcher, menu, player };
}

type TourView = ReturnType<typeof buildView>;

function setMenu(view: TourView, open: boolean, state: TourSnapshot | null): void {
  view.menu.menu.hidden = !open;
  if (open && !view.menu.menu.open) view.menu.menu.showModal();
  else if (!open) view.menu.menu.close();
  view.launcher.setAttribute('aria-expanded', String(open));
  if (open) view.menu.choices[0]?.focus();
  else if (view.menu.menu.contains(document.activeElement))
    (state ? view.player.pause : view.launcher).focus();
}

function updateView(
  view: TourView,
  state: TourSnapshot | null,
  previous: TourSnapshot | null
): void {
  const { player, launcher } = view;
  const first = previous?.id !== state?.id;
  player.player.hidden = !state;
  launcher.hidden = false;
  getUiRoot().classList.toggle('tour-active', !!state);
  if (!state) return;
  if (player.speed && player.speed.value !== String(state.rate))
    player.speed.value = String(state.rate);
  if (first) {
    setMenu(view, false, state);
    setText(player.title, state.title);
    player.seek.max = String(state.duration);
    player.pause.focus();
  }
  const label = state.paused ? 'Resume tour' : 'Pause tour';
  if (player.pause.getAttribute('aria-label') !== label) {
    setLabel(player.pause, 'aria-label', label);
    setLabel(player.pause, 'title', label);
    player.pause.replaceChildren(createIcon(state.paused ? 'play' : 'pause', 18));
  }
  player.seek.value = String(state.elapsed);
  const clock = `${time(state.elapsed)} / ${time(state.duration)}`;
  if (player.clock.textContent !== clock) {
    player.clock.textContent = clock;
    player.seek.setAttribute('aria-valuetext', clock);
  }
}

function keyHandler(view: TourView, actions: TourActions, state: () => TourSnapshot | null) {
  return (e: KeyboardEvent): void => {
    if (actions.blocked() || e.repeat) return;
    if (document.querySelector('dialog[open]') && view.menu.menu.hidden) return;
    const target = e.target instanceof Element ? e.target : null;
    if (e.code === 'Escape' && !view.menu.menu.hidden) setMenu(view, false, state());
    else {
      if (isChord(e) || target?.closest('input, textarea, select, [contenteditable="true"]'))
        return;
      if (e.code === 'KeyT') {
        actions.openGateway();
        setMenu(view, true, state());
      } else if (e.code === 'Space' && state() && !target?.closest('button, summary'))
        actions.pause();
      else return;
    }
    e.preventDefault();
    e.stopPropagation();
  };
}

function disposeTourView(
  view: TourView,
  story: ReturnType<typeof createTourStory>,
  key: (event: KeyboardEvent) => void,
  outside: (event: PointerEvent) => void
): void {
  document.removeEventListener('keydown', key, true);
  document.removeEventListener('pointerdown', outside);
  getUiRoot().classList.remove('tour-active');
  view.root.remove();
  story.dispose();
}

export function createTourControls(routes: readonly TourRoute[], actions: TourActions) {
  const view = buildView(routes, actions);
  const story = createTourStory(routes, actions);
  const { root, menu, launcher } = view;
  let snapshot: TourSnapshot | null = null;
  const key = keyHandler(view, actions, () => snapshot);
  const closeMenu = (): void => {
    setMenu(view, false, snapshot);
  };

  const outside = (e: PointerEvent): void => {
    if (e.target instanceof Node && !root.contains(e.target)) closeMenu();
  };
  launcher.addEventListener('click', () => {
    setMenu(view, !!menu.menu.hidden, snapshot);
  });
  menu.close.addEventListener('click', closeMenu);
  menu.menu.addEventListener('close', () => {
    menu.menu.hidden = true;
    launcher.setAttribute('aria-expanded', 'false');
  });
  for (const choice of menu.choices) choice.addEventListener('click', closeMenu);
  root.addEventListener('keydown', stopTourControlKey);
  root.addEventListener('keyup', stopTourControlKey);
  document.addEventListener('keydown', key, true);
  document.addEventListener('pointerdown', outside);

  return {
    mount: (parent: HTMLElement) => {
      parent.append(root);
    },
    setVisible: (visible: boolean) => {
      root.hidden = !visible;
      story.setVisible(visible);
      if (!visible) closeMenu();
    },
    update: (state: TourSnapshot | null) => {
      updateView(view, state, snapshot);
      story.update(state);
      snapshot = state;
    },
    focus: () => {
      launcher.focus();
    },
    dispose: () => {
      disposeTourView(view, story, key, outside);
    },
  };
}
